// schema.ts
import {
  boolean,
  decimal,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  serial,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { z } from 'zod';

import {
  CLINIC_TYPES,
  type ClinicSpecialty,
  COMPONENT_DB_STATUSES,
  CONTACT_TYPES,
  CONTRACT_STATUSES,
  CREDENTIALING_DOCUMENT_TYPES,
  DISTANCE_TYPES,
  GROUP_DOCUMENT_TYPES,
  INTAKE_JOB_STATUSES,
  LIFECYCLE_STATES,
  NETWORK_STATUSES,
  NOTE_TYPES,
  PROCEDURE_CATEGORIES,
  PROVIDER_GROUP_STATUSES,
  REPORT_STATUSES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from '@backend/db/const/db.const.ENUMS';

// ========== CONTRACT & TIN SCHEMA ==========

// Define the TIN structure with Zod
export const tinInfoSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  businessName: z.string().optional(),
  description: z.string().optional(),
});

// Infer the TypeScript type from the Zod schema
export type TinInfo = z.infer<typeof tinInfoSchema>;

// ========== TICKET SCHEMA ==========

// Define the subtask structure with Zod for runtime validation and type safety
export const subtaskSchema = z.object({
  id: z.string().min(1, 'Subtask ID is required'),
  title: z.string().min(1, 'Subtask title is required'),
  completed: z.boolean().default(false),
  createdAt: z.string().min(1, 'Created date is required'), // ISO date string
  updatedAt: z.string().min(1, 'Updated date is required'), // ISO date string
});

// Infer the TypeScript type from the Zod schema
export type Subtask = z.infer<typeof subtaskSchema>;

// ========== FEATURE SCHEMA ==========

// Define the feature structure with Zod for runtime validation and type safety
// Supports three states for all features:
// - Boolean features: true (enabled), false (disabled), null/undefined (unknown)
// - String features: non-empty string (enabled), empty string (disabled), null/undefined (unknown)
export const featuresSchema = z.object({
  // Staff-based features
  boardCertifiedPhysician: z.boolean().nullable().optional(),
  faa: z.boolean().nullable().optional(),
  nrcme: z.boolean().nullable().optional(),

  // Equipment features
  cbp: z.boolean().nullable().optional(),
  patl1: z.boolean().nullable().optional(),
  patl2: z.boolean().nullable().optional(),
  patl3: z.boolean().nullable().optional(),
  seafarers: z.boolean().nullable().optional(),

  // COE features
  coeCbp: z.boolean().nullable().optional(),
  coeIrs: z.boolean().nullable().optional(),
  coeRhrp: z.boolean().nullable().optional(),

  // Scheduling features
  appointments: z.boolean().nullable().optional(),
  walkIns: z.boolean().nullable().optional(),
  willSeeMinors: z.boolean().nullable().optional(),
  schedulingTypePreference: z.string().nullable().optional(),
  preferredMethodPhone: z.string().nullable().optional(),
  preferredMethodEmail: z.string().nullable().optional(),
  preferredMethodPortal: z.string().nullable().optional(),

  // Lab features
  electronicCcf: z.boolean().nullable().optional(),
  labcorpCourier: z.boolean().nullable().optional(),
  questCourier: z.boolean().nullable().optional(),
  crlCourier: z.boolean().nullable().optional(),
  otherLab: z.string().nullable().optional(),
  clia: z.string().nullable().optional(),

  // Other features
  acceptsMedicare: z.boolean().nullable().optional(),
  acceptsTriCare: z.boolean().nullable().optional(),
  adaCompliant: z.boolean().nullable().optional(),
  otherInjuryCare: z.boolean().nullable().optional(),
  urgentCare: z.boolean().nullable().optional(),

  // Agreement features
  commercial: z.boolean().nullable().optional(),
  government: z.boolean().nullable().optional(),
});

// Infer the TypeScript type from the Zod schema
export type Features = z.infer<typeof featuresSchema>;

// ========== CLINIC COMPONENTS SCHEMA ==========

// Component status enum (no 'undefined' - missing from JSON means undefined)
export const componentStatusEnum = z.enum(COMPONENT_DB_STATUSES);
export const suspendReasonEnum = z.enum([
  'Equipment Repair',
  'Calibration',
  'Out of Stock',
  'Other',
]);
// Suspend object schema
export const suspendObjectSchema = z.object({
  reason: suspendReasonEnum,
  from: z.string().datetime(), // Required start date
  until: z.string().datetime().optional(), // Optional end date (indefinite if missing)
  note: z.string().optional(), // Required when reason = "other", optional otherwise
});

// Updated component object
export const componentObjectSchema = z.object({
  status: componentStatusEnum,
  suspend: suspendObjectSchema.optional(),
});
export type ComponentObject = z.infer<typeof componentObjectSchema>;

// Clinic components: procedureId -> ComponentObject mapping
export const clinicComponentsSchema = z.record(
  z.string().min(1, 'Procedure ID is required'),
  componentObjectSchema,
);
export type ClinicComponents = z.infer<typeof clinicComponentsSchema>;

// ========== CONTACT & ADDRESS SCHEMAS ==========

// Define the address structure with Zod
export const addressSchema = z.object({
  street1: z.string().min(1, 'Street address is required'),
  street2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z
    .string()
    .min(2, 'State is required')
    .max(2, 'State must be 2 characters'),
  zipCode: z.string().min(5, 'ZIP code is required'),
  country: z.string().default('US'),
  county: z.string().optional(),
  placeId: z.string().optional(),
});

// Define a more flexible address schema for optional addresses (shipping/billing)
export const optionalAddressSchema = z
  .object({
    street1: z.string().optional(),
    street2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().optional(),
  })
  .optional();

// Define the contact structure with Zod
export const contactSchema = z.object({
  id: z.string().min(1, 'Contact ID is required'), // Required nanoid
  name: z.string().min(1, 'Contact name is required'),
  title: z.string().optional(),
  type: z.array(z.enum(CONTACT_TYPES)),
  officePhone: z.string().optional(),
  mobilePhone: z.string().optional(),
  fax: z.string().optional(),
  email: z.union([z.string().email(), z.literal('')]).optional(),
  address: addressSchema.optional(),
  active: z.boolean().default(true),
  notes: z.string().optional(),
});

// Define the document structure with Zod
export const documentSchema = z.object({
  id: z.string().min(1, 'Document ID is required'), // Required nanoid
  documentType: z.enum(GROUP_DOCUMENT_TYPES),
  customDocumentType: z.string().optional(), // Used when documentType is 'Other'
  documentName: z.string().optional(),
  effectiveDate: z.string().min(1, 'Effective date is required'), // ISO date string
  uploadDate: z.string().min(1, 'Upload date is required'), // ISO date string
  uploadedBy: z.string().min(1, 'Uploaded by user is required'),
  fileName: z.string().min(1, 'File name is required'),
  fileUrl: z.url('Valid file URL is required'),
  fileSize: z.number().positive('File size must be positive').optional(),
  mimeType: z.string().optional(),
  s3Key: z.string().optional(), // S3 object key for file storage
  deletedAt: z.string().optional(), // ISO date string when deleted
  deletedBy: z.string().optional(), // User ID who deleted the document
});

// Infer the TypeScript types from the Zod schemas
export type Address = z.infer<typeof addressSchema>;
export type OptionalAddress = z.infer<typeof optionalAddressSchema>;
export type Contact = z.infer<typeof contactSchema>;
export type Document = z.infer<typeof documentSchema>;

// ========== OPENING HOURS SCHEMA ==========

// Day schedule - open/close times (if undefined = closed)
export const dayScheduleSchema = z.object({
  open: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:MM)'),
  close: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:MM)'),
});

export type DaySchedule = z.infer<typeof dayScheduleSchema>;

// Weekly schedule - hours for each day of the week
export const weeklyScheduleSchema = z.object({
  monday: dayScheduleSchema.optional(),
  tuesday: dayScheduleSchema.optional(),
  wednesday: dayScheduleSchema.optional(),
  thursday: dayScheduleSchema.optional(),
  friday: dayScheduleSchema.optional(),
  saturday: dayScheduleSchema.optional(),
  sunday: dayScheduleSchema.optional(),
});

export type WeeklySchedule = z.infer<typeof weeklyScheduleSchema>;

// Service hours - enabled toggle + 24/7 option + weekly schedule
export const serviceHoursSchema = z.object({
  enabled: z.boolean().default(false),
  is24_7: z.boolean().default(false), // When true, ignores schedule
  schedule: weeklyScheduleSchema.optional(), // Only used when is24_7 is false
});

export type ServiceHours = z.infer<typeof serviceHoursSchema>;

// Complete opening hours - all 4 service types
export const openingHoursSchema = z.object({
  notes: z.string().optional(),
  medicalAppointments: serviceHoursSchema.optional(),
  medicalWalkIns: serviceHoursSchema.optional(),
  drugScreenAppointments: serviceHoursSchema.optional(),
  drugScreenWalkIns: serviceHoursSchema.optional(),
});

export type OpeningHours = z.infer<typeof openingHoursSchema>;

// ========== ENUMS ==========
export const procedureCategoryEnum = pgEnum(
  'procedure_category',
  PROCEDURE_CATEGORIES,
);

export const clinicTypeEnum = pgEnum('clinic_type', CLINIC_TYPES);

export const statusEnum = pgEnum('status', PROVIDER_GROUP_STATUSES);

export const networkStatusEnum = pgEnum('network_status', NETWORK_STATUSES);

// ========== NOTE ENUMS ==========
export const noteTypeEnum = pgEnum('note_type', NOTE_TYPES);

// ========== CONTRACT ENUMS ==========
export const contractStatusEnum = pgEnum('contract_status', CONTRACT_STATUSES);

// ========== TICKET ENUMS ==========
export const ticketStatusEnum = pgEnum('ticket_status', TICKET_STATUSES);

export const ticketPriorityEnum = pgEnum('ticket_priority', TICKET_PRIORITIES);

// ========== INTAKE JOB ENUMS ==========
export const intakeJobStatusEnum = pgEnum(
  'intake_job_status',
  INTAKE_JOB_STATUSES,
);

// ========== ANALYTICS REPORT ENUMS ==========
export const reportStatusEnum = pgEnum('report_status', REPORT_STATUSES);

export const distanceTypeEnum = pgEnum('distance_type', DISTANCE_TYPES);

// ========== PROCEDURES ==========
export const procedures = pgTable('procedures', {
  id: varchar('id', { length: 20 }).primaryKey(),
  cptCode: varchar('cpt_code', { length: 10 }),
  title: text('title').notNull(),
  description: text('description'),
  onFacility: boolean('on_facility').notNull().default(true),
  standardRate: decimal('standard_rate', { precision: 10, scale: 2 }).notNull(),
  category: procedureCategoryEnum('category').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== PROVIDER GROUPS ==========
export const groups = pgTable('groups', {
  id: serial('id').primaryKey(),
  groupName: varchar('group_name', { length: 255 }).notNull(),
  status: statusEnum('status').notNull().default('lead'),
  networkStatus: networkStatusEnum('network_status'),
  groupAddress: jsonb('group_address').$type<Address>(),
  features: jsonb('features').$type<Features>(),
  openingHours: jsonb('opening_hours').$type<OpeningHours>(),
  contacts: jsonb('contacts').$type<Contact[]>(),
  documents: jsonb('documents').$type<Document[]>(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== NOTES ==========
export const notes = pgTable('notes', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: varchar('user_id', { length: 255 }).notNull(), // LTS logged in user
  pocId: varchar('poc_id', { length: 255 }), // Contact ID if selected from contacts
  pocName: varchar('poc_name', { length: 255 }), // Manual POC name or contact name
  noteType: noteTypeEnum('note_type').notNull(),
  description: text('description').notNull(),
  entryDateTime: timestamp('entry_date_time').notNull(),
  clinicId: varchar('clinic_id', { length: 20 }).references(() => clinics.id, {
    onDelete: 'cascade',
  }),
  groupId: integer('group_id').references(() => groups.id, {
    onDelete: 'cascade',
  }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== CLINICS ==========

export const clinics = pgTable('clinics', {
  id: varchar('id', { length: 20 }).primaryKey(),
  groupId: integer('group_id').references(() => groups.id, {
    onDelete: 'set null',
  }),
  clinicName: varchar('clinic_name', { length: 255 }).notNull(),
  status: statusEnum('status').notNull().default('lead'),
  phone: varchar('phone', { length: 50 }),
  fax: varchar('fax', { length: 50 }),
  email: varchar('email', { length: 255 }),
  type: clinicTypeEnum('type').notNull(),
  specialties: jsonb('specialties').$type<ClinicSpecialty[]>(),
  features: jsonb('features').$type<Features>(),
  openingHours: jsonb('opening_hours').$type<OpeningHours>(),
  components: jsonb('components').$type<ClinicComponents>(),
  contacts: jsonb('contacts').$type<Contact[]>(),
  documents: jsonb('documents').$type<Document[]>(),
  // Physical address validation fields (keep validation)
  physicalText: text('physical_text'),
  physicalLat: decimal('physical_lat', { precision: 10, scale: 8 }),
  physicalLong: decimal('physical_long', { precision: 11, scale: 8 }),
  physicalVerified: jsonb('physical_verified').$type<Address>(),
  // Simple JSONB address fields (no validation)
  shippingAddress: jsonb('shipping_address').$type<Partial<Address>>(),
  billingAddress: jsonb('billing_address').$type<Partial<Address>>(),
  contractId: uuid('contract_id'),
  tinNumber: varchar('tin_number', { length: 20 }),
  networkStatus: networkStatusEnum('network_status'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== CLINIC ID COUNTERS ==========
export const clinicIdCounters = pgTable('clinic_id_counters', {
  state: varchar('state', { length: 2 }).primaryKey(),
  nextNumber: integer('next_number').notNull().default(1),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== TICKETS ==========
export const tickets = pgTable('tickets', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: text('title').notNull(),
  description: text('description'),
  status: ticketStatusEnum('status').notNull().default('todo'),
  priority: ticketPriorityEnum('priority').notNull().default('medium'),
  dueDate: timestamp('due_date'),
  assigneeId: varchar('assignee_id', { length: 255 }),
  clinicId: varchar('clinic_id', { length: 20 }).references(() => clinics.id, {
    onDelete: 'set null',
  }),
  groupId: integer('group_id').references(() => groups.id, {
    onDelete: 'set null',
  }),
  subtasks: jsonb('subtasks').$type<Subtask[]>(),
  createdBy: varchar('created_by', { length: 255 }).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== CONTRACTS ==========
export const contracts = pgTable('contracts', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: varchar('title', { length: 255 }).notNull(),
  sentDate: timestamp('sent_date'),
  receivedDate: timestamp('received_date'),
  effectiveDate: timestamp('effective_date').notNull(),
  endDate: timestamp('end_date'), // Optional if evergreen
  paymentTerms: integer('payment_terms').notNull().default(45),
  isEvergreen: boolean('is_evergreen').notNull().default(false),
  status: contractStatusEnum('status').notNull().default('draft'),

  // Reference to either a group or a clinic (similar to notes table)
  groupId: integer('group_id').references(() => groups.id, {
    onDelete: 'cascade',
  }),
  clinicId: varchar('clinic_id', { length: 20 }).references(() => clinics.id, {
    onDelete: 'cascade',
  }),

  // TINs as a jsonb record with tin number as key and company info as value
  tins: jsonb('tins').$type<Record<string, TinInfo>>(),

  // Component rates as a jsonb record with component ID as key and rate as value
  componentRates: jsonb('component_rates').$type<Record<string, number>>(),

  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== USERS ==========
export const users = pgTable('users', {
  id: varchar('id', { length: 255 }).primaryKey(), // Azure AD userId
  name: varchar('name', { length: 255 }).notNull(),
  email: varchar('email', { length: 255 }).notNull().unique(),
  firstLoginTime: timestamp('first_login_time').notNull(),
  lastLoginTime: timestamp('last_login_time').notNull(),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== INTAKE JOBS ==========
export const intakeJobs = pgTable('intake_jobs', {
  id: uuid('id').primaryKey().defaultRandom(),
  contractId: uuid('contract_id')
    .notNull()
    .references(() => contracts.id, {
      onDelete: 'cascade',
    }),
  status: intakeJobStatusEnum('status').notNull().default('processing'),
  rates: jsonb('rates')
    .$type<{ ltsCode: string; rate: number }[]>()
    .notNull()
    .default([]),
  extractedRows: jsonb('extracted_rows')
    .$type<
      {
        ltsCode: string;
        cptCode?: string;
        rate: number | null;
        selected: boolean;
        rowIndex: number;
        description?: string;
        confidence: number;
        detectionMethod?: string;
      }[]
    >()
    .notNull()
    .default([]),
  fileName: varchar('file_name', { length: 255 }).notNull(),
  s3Key: varchar('s3_key', { length: 500 }).notNull(),
  errorMessage: text('error_message'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== ANALYTICS REPORTS ==========
export const analyticsReports = pgTable('analytics_reports', {
  id: uuid('id').primaryKey().defaultRandom(),
  reportName: varchar('report_name', { length: 255 }).notNull(),
  createdBy: varchar('created_by', { length: 255 }).notNull(), // User ID who requested the report
  status: reportStatusEnum('status').notNull().default('queued'),

  // Report parameters
  addresses: jsonb('addresses')
    .$type<
      Array<{
        original: string;
        formattedAddress: string;
        lat: number;
        lng: number;
      }>
    >()
    .notNull(), // Array of geocoded address objects
  distanceType: distanceTypeEnum('distance_type').notNull(),
  maxDistance: integer('max_distance').notNull(), // Distance in miles

  // Processing progress
  totalAddresses: integer('total_addresses').notNull(),
  processedAddresses: integer('processed_addresses').notNull().default(0),

  // Results and output
  s3Key: varchar('s3_key', { length: 500 }), // S3 key for the Excel file
  fileName: varchar('file_name', { length: 255 }), // Generated filename
  errorMessage: text('error_message'),

  // Processing metadata
  startedAt: timestamp('started_at'),
  completedAt: timestamp('completed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== ANALYTICS REPORT RESULTS ==========
export const analyticsReportResults = pgTable('analytics_report_results', {
  id: uuid('id').primaryKey().defaultRandom(),
  reportId: uuid('report_id')
    .notNull()
    .references(() => analyticsReports.id, {
      onDelete: 'cascade',
    }),

  // Input address information
  inputAddress: text('input_address').notNull(),
  geocodedAddress: jsonb('geocoded_address').$type<{
    formattedAddress: string;
    lat: number;
    lng: number;
  }>(),

  // Matched clinic information (null if no matches found)
  clinicId: varchar('clinic_id', { length: 20 }).references(() => clinics.id, {
    onDelete: 'cascade',
  }),
  clinicName: varchar('clinic_name', { length: 255 }),
  clinicAddress: text('clinic_address'),
  distance: decimal('distance', { precision: 8, scale: 2 }), // Distance in miles
  drivingTime: integer('driving_time'), // Driving time in minutes (if distance_type is 'driving')

  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// ========== ROSTER JOBS ==========
export const rosterJobs = pgTable('roster_jobs', {
  id: uuid('id').primaryKey().defaultRandom(),
  clinicId: varchar('clinic_id', { length: 20 }).references(() => clinics.id, {
    onDelete: 'cascade',
  }),
  groupId: integer('group_id').references(() => groups.id, {
    onDelete: 'cascade',
  }),
  status: intakeJobStatusEnum('status').notNull().default('processing'),
  fileName: varchar('file_name', { length: 255 }).notNull(),
  s3Key: varchar('s3_key', { length: 500 }).notNull(),
  extractedProviders: jsonb('extracted_providers')
    .$type<RosterExtractedProvider[]>()
    .notNull()
    .default([]),
  errorMessage: text('error_message'),
  createdBy: varchar('created_by', { length: 255 }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Type for extracted provider data stored in rosterJobs.extractedProviders JSONB
export type RosterExtractedProvider = {
  // PDF-extracted data
  pdfLastName: string;
  pdfFirstName: string;
  pdfProviderType?: string;
  pdfCaqhNumber?: string;
  pdfNpi?: string;
  pdfDob?: string;
  pdfLicenseRaw?: string;
  pdfLicenseState?: string;
  pdfLicenseNumber?: string;
  pdfBoardCertified?: boolean;
  pdfDotCertified?: boolean;
  pdfAdverseActions?: boolean;
  // NPI enrichment data
  npiValid?: boolean;
  npiFirstName?: string;
  npiLastName?: string;
  npiProviderType?: string;
  npiLicenses?: { state: string; licenseNumber: string; licenseType: string }[];
  // Computed / matching
  existsInDb?: boolean;
  existingProviderId?: string;
  confidence?: number;
  error?: string;
};

// ========== CREDENTIALING ENUMS ==========
export const lifecycleStateEnum = pgEnum('lifecycle_state', LIFECYCLE_STATES);

export const documentTypeEnum = pgEnum(
  'document_type',
  CREDENTIALING_DOCUMENT_TYPES,
);

// ========== CREDENTIALING: PROVIDERS ==========
export const providers = pgTable('providers', {
  id: uuid('id').primaryKey().defaultRandom(),
  lastName: varchar('last_name', { length: 100 }).notNull(),
  firstName: varchar('first_name', { length: 100 }).notNull(),
  providerType: varchar('provider_type', { length: 100 }), // MD, DO, NP, PA, RN, etc.
  caqhNumber: varchar('caqh_number', { length: 50 }),
  npiNumber: varchar('npi_number', { length: 10 }).unique(),
  dateOfBirth: timestamp('date_of_birth'),
  status: varchar('status', { length: 20 }).default('active'), // Deprecated - use lifecycleState
  notes: text('notes'),
  npiData: jsonb('npi_data'), // Raw NPPES NPI Registry API response data
  lastEnrichmentAttempt: timestamp('last_enrichment_attempt'), // Track when last NPI enrichment was attempted

  // NEW: JSONB-based credentialing fields
  lifecycleState: lifecycleStateEnum('lifecycle_state').default('draft'),
  licenses: jsonb('licenses'), // Array of ProviderLicense objects
  malpracticeInsurance: jsonb('malpractice_insurance'), // MalpracticeInsurance object
  practiceLocations: jsonb('practice_locations'), // Array of PracticeLocation objects
  privilegeScope: jsonb('privilege_scope'), // PrivilegeScope object
  federalPortability: jsonb('federal_portability'), // FederalPortability object

  // Roster-sourced boolean fields
  boardCertified: boolean('board_certified'),
  dotCertified: boolean('dot_certified'),
  adverseActions: boolean('adverse_actions'),

  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// REMOVED: providerLicenses table - now stored in providers.licenses JSONB
// REMOVED: providerInsurance table - now stored in providers.malpracticeInsurance JSONB

// ========== CREDENTIALING: CLINIC PROVIDERS (Junction Table) ==========
export const clinicProviders = pgTable('clinic_providers', {
  id: uuid('id').primaryKey().defaultRandom(),
  clinicId: varchar('clinic_id', { length: 20 })
    .references(() => clinics.id, { onDelete: 'cascade' })
    .notNull(),
  providerId: uuid('provider_id')
    .references(() => providers.id, { onDelete: 'cascade' })
    .notNull(),
  isPrimary: boolean('is_primary').default(false),
  startDate: timestamp('start_date').defaultNow().notNull(),
  endDate: timestamp('end_date'),
  status: varchar('status', { length: 20 }).default('active'), // active, inactive
  notes: text('notes'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// ========== CREDENTIALING: PROVIDER DOCUMENTS ==========
export const providerDocuments = pgTable('provider_documents', {
  id: uuid('id').primaryKey().defaultRandom(),
  providerId: uuid('provider_id')
    .references(() => providers.id, { onDelete: 'cascade' })
    .notNull(),
  documentType: documentTypeEnum('document_type').notNull(),
  customDocumentType: varchar('custom_document_type', { length: 100 }), // Used when documentType = 'other'
  documentName: varchar('document_name', { length: 255 }),
  fileName: varchar('file_name', { length: 255 }).notNull(),
  fileUrl: text('file_url').notNull(),
  fileSize: integer('file_size'),
  mimeType: varchar('mime_type', { length: 100 }),
  s3Key: text('s3_key'),
  effectiveDate: timestamp('effective_date'),
  expirationDate: timestamp('expiration_date'), // Critical for compliance tracking
  uploadDate: timestamp('upload_date').defaultNow().notNull(),
  uploadedBy: varchar('uploaded_by', { length: 255 }).notNull(),
  notes: text('notes'),
  deletedAt: timestamp('deleted_at'), // Soft delete
  deletedBy: varchar('deleted_by', { length: 255 }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// REMOVED: credentialAuditLog table - dropped for simplicity
